import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Pagination,
} from '@mui/material';
import { TaskItem } from './TaskItem';
import type { TaskWithClient, TaskPagination } from '../../types/task';

interface TaskListProps {
  tasks: TaskWithClient[];
  pagination?: TaskPagination;
  isLoading?: boolean;
  error?: Error | null;
  onToggleComplete: (task: TaskWithClient) => void;
  onEdit: (task: TaskWithClient) => void;
  onDelete: (task: TaskWithClient) => void;
  onPageChange?: (page: number) => void;
  emptyMessage?: string;
}

export const TaskList: React.FC<TaskListProps> = ({
  tasks,
  pagination,
  isLoading = false,
  error = null,
  onToggleComplete,
  onEdit,
  onDelete,
  onPageChange,
  emptyMessage = 'No tasks found',
}) => {
  // Loading state
  if (isLoading && tasks.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to load tasks: {error.message}
      </Alert>
    );
  }

  // Empty state
  if (tasks.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 6,
          px: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          {emptyMessage}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Add a task using the input above
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Task List */}
      <Stack spacing={1.5}>
        {tasks.map((task) => (
          <TaskItem
            key={task._id}
            task={task}
            onToggleComplete={onToggleComplete}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        ))}
      </Stack>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={pagination.pages}
            page={pagination.page}
            onChange={(_, page) => onPageChange?.(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Loading overlay for pagination */}
      {isLoading && tasks.length > 0 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
};

export default TaskList;
